import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { submitFeedback } from '@/services/feedbackService';
import { useAuth } from '@/contexts/AuthContext';
import { StarIcon } from 'lucide-react';

const FEEDBACK_SHOWN_KEY = 'feedback_prompt_shown';
const FEEDBACK_DELAY_MS = 300000; // 5 minutes

const feedbackQuestions = [
  "What are you building today?",
  "What's frustrating you right now?",
  "How can we make this platform better for you?",
  "What feature would you love to see next?",
  "How was your experience using our platform today?"
];

const FeedbackPrompt: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [rating, setRating] = useState<number | null>(null);
  const [question, setQuestion] = useState('');
  const { toast } = useToast();
  const { user } = useAuth();
  const [sessionStartTime] = useState(Date.now());

  useEffect(() => {
    const checkAndShowFeedback = () => {
      // Only proceed if the user is logged in
      if (!user) {
        return; // Exit if no user is logged in
      }

      const lastShownTimestamp = localStorage.getItem(FEEDBACK_SHOWN_KEY);
      const shouldShowFeedback = !lastShownTimestamp || isNewDay(parseInt(lastShownTimestamp));
      
      if (shouldShowFeedback) {
        const feedbackTimer = setTimeout(() => {
          setQuestion(feedbackQuestions[Math.floor(Math.random() * feedbackQuestions.length)]);
          setOpen(true);
          // Save current timestamp
          localStorage.setItem(FEEDBACK_SHOWN_KEY, Date.now().toString());
        }, FEEDBACK_DELAY_MS);
        
        return () => clearTimeout(feedbackTimer);
      }
    };
    
    // Check if it's a new day compared to the stored timestamp
    const isNewDay = (timestamp: number): boolean => {
      if (!timestamp) return true;
      
      const lastDate = new Date(timestamp);
      const currentDate = new Date();
      
      // Compare year, month, and day to check if it's a new day
      return (
        lastDate.getFullYear() !== currentDate.getFullYear() ||
        lastDate.getMonth() !== currentDate.getMonth() ||
        lastDate.getDate() !== currentDate.getDate()
      );
    };
    
    const cleanupTimer = checkAndShowFeedback();
    
    // Return the cleanup function from useEffect
    return cleanupTimer;
  }, [user]); // Add user to the dependency array
  
  const handleSubmit = async () => {
    if (!feedback.trim()) {
      toast({
        title: "Please enter your feedback",
        variant: "destructive",
      });
      return;
    }
    
    const sessionDuration = Math.floor((Date.now() - sessionStartTime) / 1000);
    
    const result = await submitFeedback(
      {
        feedbackType: 'prompt',
        feedbackText: feedback,
        rating,
        feedbackSource: 'popup',
        sessionDuration,
      },
      user
    );
    
    if (result.success) {
      toast({
        title: "Thank you for your feedback!",
        description: "We appreciate your input and will use it to improve our platform.",
        duration: 3000,
      });
      setOpen(false);
      setFeedback('');
      setRating(null);
    } else {
      toast({
        title: "Failed to submit feedback",
        description: result.error,
        variant: "destructive",
      });
    }
  };
  
  const handleClose = () => {
    setOpen(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>We'd love your feedback!</DialogTitle>
          <DialogDescription>
            {question}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <Textarea
            placeholder="Share your thoughts with us..."
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="min-h-[100px]"
          />
          
          <div className="flex items-center justify-center space-x-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="focus:outline-none"
              >
                <StarIcon
                  className={`h-6 w-6 ${
                    rating && star <= rating 
                      ? 'text-yellow-500 fill-yellow-500' 
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
        
        <DialogFooter className="flex sm:justify-between">
          <Button 
            variant="outline" 
            size="sm" 
            className="hover:bg-primary/10 hover:text-primary transition-all hover:border-primary/30 active:scale-[0.98]"
            onClick={handleClose}
          >
            Maybe Later
          </Button>
          <Button onClick={handleSubmit}>Submit Feedback</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackPrompt; 